import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import {
  X, Send, MessageSquare, MapPin, Flame, Check, Loader2, Copy,
  ExternalLink, AlertCircle, ClipboardCopy, Target, Users2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * BulkBlastModal — one-tap "Blast top hot buyers" SMS sender.
 *
 *  1) Loads the top N hot Cash Buyers (engagement_score >= 60).
 *  2) Lets the user pick a GO/MAYBE lead to attach (its public report URL
 *     is auto-merged into the message body).
 *  3) Renders an editable message template with `{name}` + `{address}`
 *     merge fields.
 *  4) Each buyer row exposes a "Send SMS" deep-link (`sms:phone?body=…`).
 *     One tap = native dialer opens, message pre-filled.
 *  5) On send-click we log a `dispo_events` row via
 *     `POST /api/leads/{lead_id}/dispo { kind: 'sent', buyer_id, channel: 'sms' }`
 *     so the Dispo Timeline shows the outreach event automatically.
 *  6) Desktop fallback: if `sms:` isn't supported, the row offers a "Copy"
 *     button that copies the message to clipboard.
 */
export default function BulkBlastModal({ open, onClose }) {
  const [step, setStep] = useState("loading");        // loading | ready | empty | error
  const [allBuyers, setAllBuyers] = useState([]);     // full vetted buyer pool (w/ phone)
  const [selected, setSelected] = useState({});       // {buyer_id: true}
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [template, setTemplate] = useState(
    "Hey {name} — got a new deal I think you'll love.\n\n{address}\n\n{link}\n\nLet me know if you want first crack."
  );
  const [sent, setSent] = useState({});               // {buyer_id: ts}
  const [busy, setBusy] = useState({});               // {buyer_id: bool}
  // Audience filter — 'match' (uses lead.top_matches), 'hot' (top engagement_score),
  // or 'all' (every vetted buyer with a phone). Defaults to 'match' when the
  // picked lead has cached top_matches, else 'hot'.
  const [audience, setAudience] = useState("hot");
  const [audienceTouched, setAudienceTouched] = useState(false);
  // Tracks which lead IDs are currently being match-ranked in the background
  // so the Match-deal chip can render a spinner state. Ref (not state) so
  // mutations don't trigger a re-render mid-fetch and cancel the request.
  const rankingRef = useRef(new Set());
  const [rankingTick, setRankingTick] = useState(0);   // forces chip re-render
  const isRanking = (id) => !!id && rankingRef.current.has(id);

  // ── Load all vetted buyers + recent GO/MAYBE leads on open ─────────────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setStep("loading");
      try {
        const [b, l] = await Promise.all([
          api.get("/buyers?kind=vetted&sort=hot"),
          api.get("/leads"),
        ]);
        if (cancelled) return;
        const pool = (b.data || []).filter((r) => r.phone);
        setAllBuyers(pool);
        // Prefer share-enabled GO leads first; then MAYBE; cap at 25 entries
        const sortedLeads = (l.data || [])
          .filter((ld) => ld.report_share_token && ld.report_share_enabled)
          .sort((a, b2) => {
            const rank = (v) => (v === "GO" ? 0 : v === "MAYBE" ? 1 : 2);
            return rank(a.ai_verdict) - rank(b2.ai_verdict);
          })
          .slice(0, 25);
        setLeads(sortedLeads);
        if (sortedLeads.length > 0) setLeadId(sortedLeads[0].id);
        setAudienceTouched(false);
        setStep(pool.length === 0 ? "empty" : "ready");
      } catch (e) {
        if (!cancelled) setStep("error");
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const lead = useMemo(
    () => leads.find((l) => l.id === leadId) || null,
    [leads, leadId],
  );
  // Auto-rank: when the picked lead has no cached top_matches, silently fire
  // POST /api/leads/{id}/refresh-matches so the Match-deal chip lights up
  // within a beat. Works for MAYBE/PASS leads too — `refresh_top_matches`
  // server-side is not gated on ai_verdict. Idempotent — once ranked, the
  // lead's `top_matches` populates and we don't re-call.
  useEffect(() => {
    if (!lead?.id) return;
    if ((lead.top_matches?.length || 0) > 0) return;
    if (rankingRef.current.has(lead.id)) return;
    if (allBuyers.length === 0) return;   // nothing to rank against
    let cancelled = false;
    rankingRef.current.add(lead.id);
    setRankingTick((t) => t + 1);  // re-render so chip spinner appears
    (async () => {
      try {
        const { data } = await api.post(`/leads/${lead.id}/refresh-matches`);
        if (cancelled) return;
        // Merge top_matches into the in-state lead. Safer than re-fetching
        // all leads — one update, no UI churn.
        setLeads((arr) => arr.map((l) =>
          l.id === lead.id
            ? { ...l, top_matches: data.top_matches || [], top_match_count: data.top_match_count || 0 }
            : l
        ));
      } catch { /* silent — chip just stays disabled */ }
      finally {
        rankingRef.current.delete(lead.id);
        if (!cancelled) setRankingTick((t) => t + 1);  // re-render to clear spinner
      }
    })();
    return () => { cancelled = true; };
  }, [lead, allBuyers.length]);

  const shareUrl = useMemo(() => {
    if (!lead?.report_share_token) return "";
    return `${window.location.origin}/r/${lead.report_share_token}`;
  }, [lead]);
  const addressLabel = useMemo(() => {
    if (!lead) return "";
    return [lead.property_address, lead.city, lead.state].filter(Boolean).join(", ");
  }, [lead]);

  // Auto-pick the smartest audience when the lead changes (unless the user
  // has already manually overridden the audience this session).
  useEffect(() => {
    if (audienceTouched) return;
    if (lead?.top_matches?.length > 0) setAudience("match");
    else setAudience("hot");
  }, [lead, audienceTouched]);

  // Compute visible buyer rows from the full pool + the selected audience mode.
  const buyers = useMemo(() => {
    if (allBuyers.length === 0) return [];
    const hasMatches = (lead?.top_matches?.length || 0) > 0;
    // Fall back to 'hot' when 'match' is impossible (lead has no cached matches)
    const effective = audience === "match" && !hasMatches ? "hot" : audience;
    if (effective === "match" && hasMatches) {
      // Use the cached top_matches (already scored against THIS lead's buy-box).
      // Hydrate from our allBuyers pool so engagement fields are present.
      const idx = new Map(allBuyers.map((b) => [b.id, b]));
      return lead.top_matches
        .map((tm) => {
          const hydrated = idx.get(tm.id);
          return hydrated ? { ...hydrated, match_score: tm.match_score } : null;
        })
        .filter(Boolean)
        .slice(0, 5);
    }
    if (effective === "hot") {
      const hot = allBuyers.filter((r) => (r.engagement_score || 0) >= 60).slice(0, 5);
      if (hot.length >= 2) return hot;
      return allBuyers.slice(0, 5);   // widen if not enough hot signal
    }
    // 'all' — top 10 vetted buyers, by engagement_score desc (server already sorted)
    return allBuyers.slice(0, 10);
  }, [allBuyers, audience, lead]);

  // Re-sync `selected` whenever the buyer list shape changes — default everyone
  // to checked. Preserves prior toggles for buyers still in view.
  useEffect(() => {
    setSelected((prev) => {
      const next = {};
      for (const b of buyers) next[b.id] = prev[b.id] !== false; // default true
      return next;
    });
  }, [buyers]);

  const renderBody = (buyer) => {
    return template
      .replaceAll("{name}", (buyer.name || "").split(" ")[0] || "there")
      .replaceAll("{address}", addressLabel || "a fresh deal")
      .replaceAll("{link}", shareUrl || "");
  };

  const handleSend = (buyer) => {
    setBusy((b) => ({ ...b, [buyer.id]: true }));
    const body = renderBody(buyer);
    // iOS prefers '&'; Android prefers '?'. Modern browsers accept both —
    // we use ?body= which works on iOS 15+, Android, and macOS.
    const href = `sms:${buyer.phone}?body=${encodeURIComponent(body)}`;
    try {
      const w = window.open(href, "_blank");
      if (!w) window.location.href = href;
    } catch { /* fall through */ }
    // Fire-and-forget dispo log so the timeline reflects the blast.
    if (lead?.id) {
      api.post(`/leads/${lead.id}/dispo`, {
        kind: "sent",
        buyer_id: buyer.id,
        buyer_name: buyer.name,
        channel: "sms",
        notes: "Bulk blast from Buyers CRM",
      }).catch(() => { /* don't block UX on tracking */ });
    }
    setTimeout(() => {
      setSent((s) => ({ ...s, [buyer.id]: Date.now() }));
      setBusy((b) => ({ ...b, [buyer.id]: false }));
    }, 250);
  };

  const handleCopy = async (buyer) => {
    try {
      await navigator.clipboard.writeText(renderBody(buyer));
      toast.success(`Copied for ${buyer.name}`);
    } catch {
      toast.error("Couldn't copy — long-press the message instead.");
    }
  };

  // Copy ALL selected (not-yet-sent) messages as a single block, separated by
  // visual dividers + buyer-name/phone headers. User pastes once into Notes,
  // then fires off 5 texts in under a minute. We also fire-and-forget a dispo
  // event per buyer so the timeline reflects the blast.
  const handleCopyAll = async () => {
    const targets = buyers.filter((b) => selected[b.id] && !sent[b.id]);
    if (targets.length === 0) {
      toast.error("Nothing to copy — pick at least one buyer.");
      return;
    }
    const blocks = targets.map((b, i) => {
      const header = `─── ${i + 1}/${targets.length} · ${b.name} · ${b.phone} ───`;
      return `${header}\n${renderBody(b)}`;
    });
    const blob = blocks.join("\n\n");
    try {
      await navigator.clipboard.writeText(blob);
      toast.success(`Copied ${targets.length} message${targets.length === 1 ? "" : "s"} — paste into Notes`);
    } catch {
      toast.error("Couldn't copy — try the per-row Copy buttons.");
      return;
    }
    // Mark each as sent + log dispo. Same fire-and-forget pattern as Send-SMS.
    const stampedAt = Date.now();
    setSent((s) => {
      const next = { ...s };
      for (const b of targets) next[b.id] = stampedAt;
      return next;
    });
    if (lead?.id) {
      targets.forEach((b) => {
        api.post(`/leads/${lead.id}/dispo`, {
          kind: "sent",
          buyer_id: b.id,
          buyer_name: b.name,
          channel: "sms",
          notes: "Bulk-blast (copy all) from Buyers CRM",
        }).catch(() => { /* don't block UX on tracking */ });
      });
    }
  };

  const eligibleCount = buyers.filter((b) => selected[b.id]).length;
  const sentCount = Object.keys(sent).length;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm p-3 md:p-6"
      onClick={onClose}
      data-testid="bulk-blast-overlay"
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-blast-modal"
      >
        {/* ── Header ────────────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600 text-white p-5 md:p-6 flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] tracking-widest font-extrabold uppercase bg-white/15 border border-white/20 backdrop-blur">
              <Flame size={11} /> Hot Blast
            </div>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mt-2 leading-tight">
              Blast your top hot buyers
            </h2>
            <p className="text-white/85 text-sm mt-1.5 max-w-md">
              Pre-filled SMS to your most-engaged Cash Buyers. One tap = your phone&apos;s native messages app opens, ready to send.
            </p>
          </div>
          <button
            type="button"
            className="rounded-full p-2 bg-white/10 hover:bg-white/20 transition-colors"
            onClick={onClose}
            data-testid="bulk-blast-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className="p-5 md:p-6">
          {step === "loading" && (
            <div className="text-center py-12 text-[var(--ink-2)]">
              <Loader2 className="animate-spin mx-auto mb-3" /> Loading your hot buyers…
            </div>
          )}

          {step === "error" && (
            <div className="text-center py-12 text-rose-600">
              <AlertCircle className="mx-auto mb-2" />
              Couldn&apos;t load. Try closing and reopening.
            </div>
          )}

          {step === "empty" && (
            <div className="text-center py-10" data-testid="bulk-blast-empty">
              <Flame size={28} className="text-[var(--ink-3)] mx-auto mb-3" />
              <div className="font-display font-bold text-lg">No hot Cash Buyers yet</div>
              <p className="text-sm text-[var(--ink-2)] mt-2 max-w-md mx-auto">
                Hot buyers are Cash Buyers with recent photo views or deal matches. Share a few property reports to start building engagement signal — they&apos;ll show up here automatically.
              </p>
            </div>
          )}

          {step === "ready" && (
            <>
              {/* Step 1 — Pick the deal */}
              <Section number={1} title="Pick the deal">
                {leads.length === 0 ? (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="bulk-blast-no-leads">
                    No share-enabled leads yet. Open a Property Report and toggle <strong>Share publicly</strong> first — then re-open this blast.
                  </div>
                ) : (
                  <select
                    value={leadId}
                    onChange={(e) => setLeadId(e.target.value)}
                    className="select w-full"
                    data-testid="bulk-blast-lead-picker"
                  >
                    {leads.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.ai_verdict ? `[${l.ai_verdict}] ` : ""}{l.property_address}
                        {l.city ? `, ${l.city}` : ""}
                        {l.estimated_arv ? ` — ARV ${fmtMoney(l.estimated_arv)}` : ""}
                      </option>
                    ))}
                  </select>
                )}
                {shareUrl && (
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline mt-2"
                    data-testid="bulk-blast-preview-link"
                  >
                    Preview report <ExternalLink size={11} />
                  </a>
                )}
              </Section>

              {/* Step 2 — Message template */}
              <Section number={2} title="Your message">
                <textarea
                  className="textarea w-full font-mono text-[13px]"
                  rows={5}
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  data-testid="bulk-blast-template"
                />
                <div className="text-[11px] text-[var(--ink-3)] mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                  <span><code className="bg-slate-100 px-1 rounded">{"{name}"}</code> buyer&apos;s first name</span>
                  <span><code className="bg-slate-100 px-1 rounded">{"{address}"}</code> deal address</span>
                  <span><code className="bg-slate-100 px-1 rounded">{"{link}"}</code> public report URL</span>
                </div>
              </Section>

              {/* Step 3 — Buyer rows */}
              <Section
                number={3}
                title={`Send (${eligibleCount} ready · ${sentCount} sent)`}
              >
                {/* Audience chip row — pick WHO gets the blast */}
                <div className="bulk-blast-audience" data-testid="bulk-blast-audience">
                  <AudienceChip
                    active={audience === "match"}
                    onClick={() => { setAudience("match"); setAudienceTouched(true); }}
                    Icon={Target}
                    label="Match deal"
                    sub={
                      isRanking(lead?.id)
                        ? "ranking…"
                        : lead?.top_matches?.length > 0
                          ? `${Math.min(5, lead.top_matches.length)} pre-ranked`
                          : "no match yet"
                    }
                    disabled={!lead?.top_matches?.length}
                    spinning={isRanking(lead?.id)}
                    testid="audience-match"
                  />
                  <AudienceChip
                    active={audience === "hot"}
                    onClick={() => { setAudience("hot"); setAudienceTouched(true); }}
                    Icon={Flame}
                    label="Hot buyers"
                    sub={`top ${Math.min(5, allBuyers.filter((b) => (b.engagement_score || 0) >= 60).length || allBuyers.length)} engaged`}
                    testid="audience-hot"
                  />
                  <AudienceChip
                    active={audience === "all"}
                    onClick={() => { setAudience("all"); setAudienceTouched(true); }}
                    Icon={Users2}
                    label="All vetted"
                    sub={`${Math.min(10, allBuyers.length)} of ${allBuyers.length}`}
                    testid="audience-all"
                  />
                </div>
                <ul className="space-y-2" data-testid="bulk-blast-buyers">
                  {buyers.map((b) => {
                    const wasSent = !!sent[b.id];
                    const isBusy = !!busy[b.id];
                    return (
                      <li
                        key={b.id}
                        className={
                          "border rounded-xl p-3 flex items-center gap-3 transition-all " +
                          (wasSent
                            ? "bg-emerald-50 border-emerald-300"
                            : "bg-white border-[var(--line)] hover:border-[var(--ink)]")
                        }
                        data-testid={`bulk-blast-row-${b.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!selected[b.id]}
                          onChange={(e) =>
                            setSelected((s) => ({ ...s, [b.id]: e.target.checked }))
                          }
                          className="w-4 h-4"
                          data-testid={`bulk-blast-check-${b.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold">{b.name}</span>
                            {(b.engagement_score || 0) >= 60 && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-rose-500 text-white">
                                <Flame size={9} /> {b.engagement_score}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[var(--ink-2)] truncate">
                            {b.phone}
                            {b.deals_matched_count > 0 && (
                              <span className="ml-2 text-emerald-700">
                                · {b.deals_matched_count} match{b.deals_matched_count === 1 ? "" : "es"}
                              </span>
                            )}
                          </div>
                        </div>
                        {wasSent ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 px-2.5 py-1.5">
                            <Check size={14} /> Sent
                          </span>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              className="btn-ghost"
                              onClick={() => handleCopy(b)}
                              title="Copy message to clipboard"
                              data-testid={`bulk-blast-copy-${b.id}`}
                              disabled={!selected[b.id] || isBusy}
                            >
                              <Copy size={13} />
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 transition disabled:opacity-50"
                              onClick={() => handleSend(b)}
                              disabled={!selected[b.id] || isBusy || (leads.length > 0 && !lead)}
                              data-testid={`bulk-blast-send-${b.id}`}
                            >
                              {isBusy ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Send size={12} />
                              )}
                              Send SMS
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Section>

              {/* Copy-all super-button — paste once, text 5 in a minute */}
              {buyers.some((b) => selected[b.id] && !sent[b.id]) && (
                <button
                  type="button"
                  onClick={handleCopyAll}
                  className="copy-all-btn"
                  data-testid="bulk-blast-copy-all"
                >
                  <ClipboardCopy size={16} />
                  <div className="text-left flex-1">
                    <div className="font-extrabold text-sm">Copy all messages at once</div>
                    <div className="text-[11px] opacity-85 font-medium">
                      Paste into Notes → forward {Object.keys(selected).filter((id) => selected[id] && !sent[id]).length} quick texts from your phone
                    </div>
                  </div>
                </button>
              )}

              {/* Helper footer */}
              <div className="mt-5 pt-4 border-t border-[var(--line)] text-xs text-[var(--ink-2)] flex items-start gap-2">
                <MessageSquare size={13} className="mt-0.5 shrink-0" />
                <div>
                  Each Send opens your native Messages app pre-filled. On desktop, use <strong>Copy</strong> and text from your phone. Every send is logged on the deal&apos;s Dispo Timeline.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2.5">
        <span className="w-6 h-6 rounded-full bg-[var(--ink)] text-white flex items-center justify-center font-bold text-xs">
          {number}
        </span>
        <h3 className="font-display font-bold text-base">{title}</h3>
      </div>
      <div className="ml-8">{children}</div>
    </div>
  );
}

function AudienceChip({ active, onClick, Icon, label, sub, disabled, spinning, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={"audience-chip" + (active ? " active" : "") + (disabled ? " is-disabled" : "") + (spinning ? " is-ranking" : "")}
      data-testid={testid}
      aria-pressed={active}
    >
      {spinning ? (
        <Loader2 size={13} className="audience-chip-icon animate-spin" />
      ) : (
        <Icon size={13} className="audience-chip-icon" />
      )}
      <div className="text-left leading-tight">
        <div className="text-[12px] font-bold">{label}</div>
        <div className="text-[10px] opacity-70 font-medium">{sub}</div>
      </div>
    </button>
  );
}

// Wholesaler-facing hero pill that opens the blast modal. Exported separately
// so it can be dropped into Buyers.jsx hero or anywhere else.
export function BulkBlastButton({ hot, onClick }) {
  const disabled = hot < 1;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "bulk-blast-cta " + (disabled ? "opacity-40 cursor-not-allowed" : "")
      }
      data-testid="bulk-blast-open"
      title={disabled ? "Add Cash Buyers + share reports to build engagement signal" : `Blast your ${Math.min(5, hot)} hot buyers`}
    >
      <Flame size={14} />
      <span className="whitespace-nowrap">Blast Hot Buyers</span>
      {hot > 0 && (
        <span className="bulk-blast-count">{Math.min(5, hot)}</span>
      )}
    </button>
  );
}
